/**
 * @fileoverview Multi-student Teacher Final Assessment Drawer (AR-579).
 *
 * Replaces the legacy single-student `TaskTrackerFinalAssessmentDrawer`.
 * Full-screen drawer per the Figma: read-only "Project: <name>", an
 * "All students" selector, and the project standards/skills as cards with the
 * 5-hand picker. Scores auto-save on change; bulk-assess applies a score to
 * every visible student of an item.
 *
 * Layout reuses the lesson drawer's atoms (ScorePicker / TeacherAssessmentInput
 * / SelfAssessmentInfo / BulkAssessRow / StudentMultiSelect / column widths) so
 * it matches the existing Teacher Assessment Drawer one-to-one.
 */

import { RefObject, useRef } from 'react'
import { ActionIcon, Box, Center, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core'
import { IconX } from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import LumiarDrawer from 'components/LumiarDrawer/LumiarDrawer'
import { AssessmentHistoryModal } from 'components'
import { AssessmentNotesSection } from '../teacherAssessmentDrawer/components/AssessmentNotesSection'
import { OverwriteConfirmModal } from '../teacherAssessmentDrawer/components/OverwriteConfirmModal'
import { StudentMultiSelect } from '../teacherAssessmentDrawer/components/StudentMultiSelect'
import { FinalAssessmentItemCard } from './components/FinalAssessmentItemCard'
import { updateFinalAssessmentJournal } from './service'
import { useFinalAssessmentDrawer } from './useFinalAssessmentDrawer'
import { useLessonAssessmentsHistory } from './useLessonAssessmentsHistory'
import type { FinalAssessmentItem } from './types'

export interface TeacherFinalAssessmentDrawerProps {
  opened: boolean
  projectId: string
  projectName: string
  /** When supplied, that student is the only one selected on open. */
  initialStudentId?: string | null
  onClose: () => void
  /** Called after the drawer closes; parent typically refreshes its grid. */
  onClosed?: () => void
}

export function TeacherFinalAssessmentDrawer({
  opened,
  projectId,
  projectName,
  initialStudentId,
  onClose,
  onClosed,
}: TeacherFinalAssessmentDrawerProps) {
  const { t } = useTranslation()
  const modalRef = useRef<HTMLElement | null>(null)

  const {
    loading,
    data,
    selectedStudentIds,
    visibleStudents,
    studentOptions,
    setStudentSelection,
    overwritePrompt,
    dismissOverwritePrompt,
    confirmOverwritePrompt,
    setStudentScore,
    applyBulkScore,
  } = useFinalAssessmentDrawer({ opened, projectId, projectName, initialStudentId })

  const history = useLessonAssessmentsHistory(projectId)

  // Teacher notes (AR-753) are per-student (master journal), so the section
  // only renders when exactly one student is selected (bulk mode awaits a UX
  // definition). Journal data already comes with the per-student details —
  // no extra fetch needed.
  const singleStudent = visibleStudents.length === 1 ? visibleStudents[0] : null

  const handleDrawerChange = (value: boolean) => {
    if (value) return
    onClose()
    onClosed?.()
  }

  const renderItems = (items: FinalAssessmentItem[]) =>
    items.map(item => (
      <FinalAssessmentItemCard
        key={item.lv4ItemId}
        item={item}
        visibleStudents={visibleStudents}
        onStudentScoreChange={(studentId, score) => void setStudentScore(item.lv4ItemId, studentId, score)}
        onBulkScore={score => void applyBulkScore(item.lv4ItemId, score)}
        onViewLessonsAssessments={studentId => {
          const student = data?.students.find(s => s.id === studentId)
          if (student) void history.openHistory(student, item.lv4ItemId)
        }}
      />
    ))

  return (
    <>
      <LumiarDrawer
        visibility={opened}
        onChange={handleDrawerChange}
        side="right"
        modalRef={modalRef as RefObject<HTMLDivElement>}
        scrollable={false}
      >
        <Box style={{ width: '100vw', height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Group
            position="apart"
            align="center"
            px="lg"
            py="md"
            style={{ borderBottom: '1px solid var(--mantine-color-gray-2)', flexShrink: 0 }}
          >
            <Title order={3}>{t('arc_final_assessment_drawer.title')}</Title>
            <ActionIcon
              variant="subtle"
              onClick={() => handleDrawerChange(false)}
              aria-label={t('arc_teacher_assessment_drawer.close_aria')}
            >
              <IconX size={20} />
            </ActionIcon>
          </Group>

          <Box style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
            <Stack spacing="lg" p="lg" style={{ width: '100%', boxSizing: 'border-box' }}>
              {loading && (
                <Center py="xl">
                  <Loader size="md" />
                </Center>
              )}

              {!loading && data && (
                <>
                  <Paper withBorder p="xs" radius="sm">
                    <Group spacing={4}>
                      <Text size="sm" color="dimmed">
                        {t('arc_final_assessment_drawer.project_label')}:
                      </Text>
                      <Text size="sm">{data.project.name}</Text>
                    </Group>
                  </Paper>

                  <StudentMultiSelect
                    options={studentOptions}
                    value={selectedStudentIds}
                    onChange={setStudentSelection}
                    disabled={data.students.length === 0}
                  />

                  {data.standards.length === 0 && data.skills.length === 0 && (
                    <Text color="dimmed" align="center" py="xl">
                      {t('arc_teacher_assessment_drawer.no_curriculum_items')}
                    </Text>
                  )}

                  {data.standards.length > 0 && (
                    <Stack spacing="md">
                      <Title order={5}>{t('arc_teacher_assessment_drawer.standards')}</Title>
                      {renderItems(data.standards)}
                    </Stack>
                  )}

                  {data.skills.length > 0 && (
                    <Stack spacing="md">
                      <Title order={5}>{t('arc_teacher_assessment_drawer.skills')}</Title>
                      {renderItems(data.skills)}
                    </Stack>
                  )}

                  {singleStudent?.journalId && (
                    <AssessmentNotesSection
                      key={`${singleStudent.id}:${singleStudent.journalId}`}
                      initialValue={singleStudent.journal}
                      onPersist={description =>
                        updateFinalAssessmentJournal(singleStudent.journalId, description)
                      }
                    />
                  )}
                </>
              )}

              {!loading && !data && (
                <Center py="xl">
                  <Text color="dimmed">{t('arc_teacher_assessment_drawer.error.load_failed')}</Text>
                </Center>
              )}
            </Stack>
          </Box>
        </Box>
      </LumiarDrawer>

      <OverwriteConfirmModal
        opened={overwritePrompt !== null}
        affectedCount={overwritePrompt?.affectedStudentIds.length ?? 0}
        onCancel={dismissOverwritePrompt}
        onConfirm={confirmOverwritePrompt}
      />

      <AssessmentHistoryModal
        openedHistoryModal={history.opened}
        closeHistoryModal={history.closeHistory}
        historyModalContent={history.entries}
        userName={history.student?.fullName ?? ''}
        avatarURL={history.student?.avatarUrl ?? ''}
        loading={history.loading}
      />
    </>
  )
}
